import React, { useState } from 'react';
import SmartFieldMapper from './SmartFieldMapper';
import SmartFieldMapperUI from './SmartFieldMapperUI';

/**
 * Smart Field Mapper Integration for Clara360 Onboarding
 * =======================================================
 * Integrates the Smart Field Mapper into the onboarding workflow
 * for intelligent data import from Excel, PDF, and DATEV sources.
 */

/**
 * Excel/CSV Field Mapper Component
 * Analyzes Excel columns and maps them to database schema
 */
export const ExcelFieldMapper = ({ 
  excelData, 
  onMappingComplete, 
  onCancel 
}) => {
  const [sourceFields, setSourceFields] = useState([]);
  const [detectedLanguage, setDetectedLanguage] = useState('de');

  React.useEffect(() => {
    if (excelData && excelData.length > 0) {
      // Extract column names from first row
      const columns = Object.keys(excelData[0]);
      setSourceFields(columns);

      // Auto-detect language based on field names
      const language = detectLanguage(columns);
      setDetectedLanguage(language);
    }
  }, [excelData]);

  const detectLanguage = (fields) => {
    const germanKeywords = ['mieter', 'eigentümer', 'wohnung', 'straße', 'stadt', 'plz'];
    const englishKeywords = ['tenant', 'owner', 'apartment', 'street', 'city', 'zip'];

    const normalizedFields = fields.map(f => f.toLowerCase()).join(' ');
    
    const germanMatches = germanKeywords.filter(k => normalizedFields.includes(k)).length;
    const englishMatches = englishKeywords.filter(k => normalizedFields.includes(k)).length;

    return germanMatches > englishMatches ? 'de' : 'en';
  };

  const handleMappingComplete = (mappingResult) => {
    // Transform Excel data according to mapping
    const transformedData = transformExcelData(excelData, mappingResult.mappings);
    
    if (onMappingComplete) {
      onMappingComplete({
        data: transformedData,
        mappings: mappingResult.mappings,
        quality: mappingResult.quality,
        unmappedFields: mappingResult.unmappedFields
      });
    }
  };

  const transformExcelData = (rawData, mappings) => {
    return rawData.map(row => {
      const transformedRow = {};
      
      mappings.forEach(mapping => {
        const sourceValue = row[mapping.sourceField];
        if (sourceValue !== undefined && sourceValue !== null && sourceValue !== '') {
          transformedRow[mapping.targetField] = sourceValue;
        }
      });

      return transformedRow;
    });
  };

  if (!sourceFields.length) {
    return (
      <div className="p-8 text-center">
        <p className="text-gray-600">Keine Spalten gefunden</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="mb-4">
        <h2 className="text-xl font-bold text-gray-900 mb-2">
          Excel-Spalten zuordnen
        </h2>
        <p className="text-sm text-gray-600">
          Erkannte Sprache: <span className="font-medium">{detectedLanguage === 'de' ? 'Deutsch' : 'English'}</span>
          {' • '}
          {sourceFields.length} Spalten gefunden
        </p>
      </div>

      <SmartFieldMapperUI
        sourceFields={sourceFields}
        targetTable="unit" // Default to unit table for tenant lists
        language={detectedLanguage}
        onMappingComplete={handleMappingComplete}
        onCancel={onCancel}
      />
    </div>
  );
};

/**
 * PDF Contract Field Mapper Component
 * Maps extracted contract data to database schema
 */
export const PDFContractMapper = ({ 
  extractedData, 
  onMappingComplete, 
  onCancel 
}) => {
  const sourceFields = Object.keys(extractedData);

  const handleMappingComplete = (mappingResult) => {
    // Transform extracted data according to mapping
    const transformedData = {};
    
    mappingResult.mappings.forEach(mapping => {
      const sourceValue = extractedData[mapping.sourceField];
      if (sourceValue !== undefined && sourceValue !== null && sourceValue !== '') {
        transformedData[mapping.targetField] = sourceValue;
      }
    });

    if (onMappingComplete) {
      onMappingComplete({
        data: transformedData,
        mappings: mappingResult.mappings,
        quality: mappingResult.quality,
        unmappedFields: mappingResult.unmappedFields
      });
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="mb-4">
        <h2 className="text-xl font-bold text-gray-900 mb-2">
          Vertragsdaten zuordnen
        </h2>
        <p className="text-sm text-gray-600">
          {sourceFields.length} Felder aus PDF extrahiert
        </p>
      </div>

      <SmartFieldMapperUI
        sourceFields={sourceFields}
        targetTable="tenant" // Contracts typically map to tenant data
        language="de"
        onMappingComplete={handleMappingComplete}
        onCancel={onCancel}
      />
    </div>
  );
};

/**
 * DATEV Export Field Mapper Component
 * Maps DATEV export fields to database schema
 */
export const DATEVFieldMapper = ({ 
  datevData, 
  onMappingComplete, 
  onCancel 
}) => {
  const [sourceFields, setSourceFields] = useState([]);

  React.useEffect(() => {
    if (datevData && datevData.length > 0) {
      const columns = Object.keys(datevData[0]);
      setSourceFields(columns);
    }
  }, [datevData]);

  const handleMappingComplete = (mappingResult) => {
    const transformedData = datevData.map(row => {
      const transformedRow = {};
      
      mappingResult.mappings.forEach(mapping => {
        const sourceValue = row[mapping.sourceField];
        if (sourceValue !== undefined && sourceValue !== null && sourceValue !== '') {
          transformedRow[mapping.targetField] = sourceValue;
        }
      });

      return transformedRow;
    });

    if (onMappingComplete) {
      onMappingComplete({
        data: transformedData,
        mappings: mappingResult.mappings,
        quality: mappingResult.quality,
        unmappedFields: mappingResult.unmappedFields
      });
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="mb-4">
        <h2 className="text-xl font-bold text-gray-900 mb-2">
          DATEV-Export zuordnen
        </h2>
        <p className="text-sm text-gray-600">
          {sourceFields.length} Spalten aus DATEV-Export
        </p>
      </div>

      <SmartFieldMapperUI
        sourceFields={sourceFields}
        targetTable="property" // DATEV typically contains property/accounting data
        language="de"
        onMappingComplete={handleMappingComplete}
        onCancel={onCancel}
      />
    </div>
  );
};

/**
 * Multi-Table Field Mapper
 * Intelligent mapper that detects target table based on field patterns
 */
export const MultiTableFieldMapper = ({ 
  sourceData, 
  onMappingComplete, 
  onCancel 
}) => {
  const [targetTable, setTargetTable] = useState(null);
  const [sourceFields, setSourceFields] = useState([]);

  React.useEffect(() => {
    if (sourceData && sourceData.length > 0) {
      const columns = Object.keys(sourceData[0]);
      setSourceFields(columns);

      // Auto-detect target table
      const detectedTable = detectTargetTable(columns);
      setTargetTable(detectedTable);
    }
  }, [sourceData]);

  const detectTargetTable = (fields) => {
    const normalizedFields = fields.map(f => f.toLowerCase()).join(' ');

    // Scoring for each table type
    const scores = {
      owner: 0,
      property: 0,
      unit: 0,
      tenant: 0
    };

    // Owner indicators
    if (normalizedFields.match(/eigentümer|vermieter|owner|landlord/)) scores.owner += 3;
    if (normalizedFields.match(/firma|company|tax_id|steuernummer/)) scores.owner += 2;

    // Property indicators
    if (normalizedFields.match(/objekt|immobilie|property|building/)) scores.property += 3;
    if (normalizedFields.match(/baujahr|year_built|adresse|address/)) scores.property += 2;

    // Unit indicators
    if (normalizedFields.match(/wohnung|einheit|unit|apartment/)) scores.unit += 3;
    if (normalizedFields.match(/miete|rent|größe|size|zimmer|rooms/)) scores.unit += 2;

    // Tenant indicators
    if (normalizedFields.match(/mieter|tenant|bewohner|renter/)) scores.tenant += 3;
    if (normalizedFields.match(/mietbeginn|lease_start|kaution|deposit/)) scores.tenant += 2;

    // Return table with highest score
    const maxScore = Math.max(...Object.values(scores));
    const detectedTable = Object.keys(scores).find(key => scores[key] === maxScore);

    return detectedTable || 'unit'; // Default to unit if unclear
  };

  const handleMappingComplete = (mappingResult) => {
    const transformedData = sourceData.map(row => {
      const transformedRow = {};
      
      mappingResult.mappings.forEach(mapping => {
        const sourceValue = row[mapping.sourceField];
        if (sourceValue !== undefined && sourceValue !== null && sourceValue !== '') {
          transformedRow[mapping.targetField] = sourceValue;
        }
      });

      return transformedRow;
    });

    if (onMappingComplete) {
      onMappingComplete({
        data: transformedData,
        mappings: mappingResult.mappings,
        quality: mappingResult.quality,
        unmappedFields: mappingResult.unmappedFields,
        detectedTable: targetTable
      });
    }
  };

  if (!targetTable) {
    return (
      <div className="p-8 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
        <p className="text-gray-600">Analysiere Datenstruktur...</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="mb-4">
        <h2 className="text-xl font-bold text-gray-900 mb-2">
          Intelligentes Feld-Mapping
        </h2>
        <p className="text-sm text-gray-600">
          Erkannter Datentyp: <span className="font-medium capitalize">{targetTable}</span>
          {' • '}
          {sourceFields.length} Felder gefunden
        </p>
      </div>

      <SmartFieldMapperUI
        sourceFields={sourceFields}
        targetTable={targetTable}
        language="de"
        onMappingComplete={handleMappingComplete}
        onCancel={onCancel}
      />
    </div>
  );
};

export default {
  ExcelFieldMapper,
  PDFContractMapper,
  DATEVFieldMapper,
  MultiTableFieldMapper
};

